import React, { useState, useRef, useEffect } from "react"
import classNames from "classnames"

import { Action, ActionType } from "@tenforce/toolbox-button-push-react"
import { Modal } from "@tenforce/toolbox-modal-react"
import { Symbols } from "@tenforce/toolbox-fontmap"
import { TextBig } from "@tenforce/product-fields"

import { GlobalState } from "../../../store"
import { useAppSelector } from "../../../hooks"

import Separator from "../../common/Separator"

import {
    ICheckListViewActions,
    ICheckListViewContextMenuType,
    ICheckListViewControls,
} from ".."

export interface Props {
    type: ICheckListViewContextMenuType
    resourceId: string
    controls: ICheckListViewControls
    contextMenuActions: ICheckListViewActions
    remark: string
}

export default function ContextMenu({
    contextMenuActions,
    controls: {
        canCreateNewAction,
        canCreateNewFinding,
        canLinkExistingAction,
        canLinkExistingFinding,
        canEditRemark,
    },
    type,
    resourceId,
    remark,
}: Props): JSX.Element {
    const { labels } = useAppSelector((state: GlobalState) => state.audits)

    const targetRef = useRef<HTMLButtonElement>(null)

    const [isVisible, setIsVisible] = useState(false)
    const [unsyncRemark, setUnsyncRemark] = useState(remark)

    useEffect(() => {
        setUnsyncRemark(remark)
    }, [remark])

    const isFinding = type === "Finding"
    const isTopic = type === "Topic"
    const isQuestion = type === "Question"

    // remark field should only be visible for questions where it wasn't field before
    // this ensure that "empty" fields are not shown in the card view of the Question
    // but when it already has value, it's no longer an empty field anymore
    // and non-empty fields show up in the cardview instead of the context menu
    const isRemarkEditingAllowed =
        isQuestion && !!canEditRemark && remark.length === 0

    const hasFirstSection = isRemarkEditingAllowed
    const hasSecondSection = !!canCreateNewAction || !!canLinkExistingAction
    const hasThirdSection =
        !isFinding && (canCreateNewFinding || canLinkExistingFinding)

    // comes together with remark field all the time
    const hasFirstSeparator = hasSecondSection || hasThirdSection

    // between actions and and findings
    // should not be visible betweek remark and findings,
    // as the first separator takes care of that
    const hasSecondSeparator = hasSecondSection && hasThirdSection

    // CONTEXT MENU VISIBILITY
    // if no actions are allowed then we should not show the context menu either
    if (!hasFirstSection && !hasSecondSection && !hasThirdSection) {
        return <></>
    }

    const buttonAlignmentClasses = "tw-px-4 tw-py-2"
    const separator = <Separator />

    const hideModal = (): void => {
        // instead of saving the remark on blur
        // it should save only, when the context menu is closed
        if (isRemarkEditingAllowed && unsyncRemark !== remark) {
            contextMenuActions.updateRemark(resourceId, unsyncRemark)
        }

        setIsVisible(false)
    }

    const actionClasses = classNames({
        "is-hidden-action": !isTopic,
        "is-global-hidden-action": isTopic,
    })

    return (
        <>
            <Action
                data-ft-component="showContextMenu"
                actionType={ActionType.icon}
                onClick={(): void => {
                    setIsVisible(true)
                }}
                ref={targetRef}
                extraClasses={actionClasses}
                title={labels.ContextMenu}
            >
                {Symbols.EllipsisVertical}
            </Action>
            <Modal
                isShown={isVisible}
                containerProps={{
                    targetRef,
                    id: "auditsContextMenu",
                    onClose: hideModal,
                }}
                innerContentProps={{
                    extraClasses: "bg-white",
                }}
            >
                <div className="tw-flex tw-flex-col tw-py-2 tw-gap-2">
                    {isRemarkEditingAllowed && (
                        <div data-ft-component="remarkInput">
                            <TextBig
                                value={unsyncRemark}
                                title={labels.Remark}
                                placeholder={labels.RemarkPlaceholder}
                                fieldId="remarkInput"
                                isLoading={false}
                                onSave={(value): void => {
                                    setUnsyncRemark(value)
                                }}
                            />
                            {hasFirstSeparator && separator}
                        </div>
                    )}

                    {!!canCreateNewAction && (
                        <Action
                            actionType={ActionType.block}
                            extraClasses={buttonAlignmentClasses}
                            title={labels.AddNewAction}
                            data-ft-component="addNewAction"
                            onClick={(): void => {
                                switch (type) {
                                    case "Topic":
                                        contextMenuActions.addNewActionToTopic(
                                            resourceId,
                                        )
                                        break
                                    case "Question":
                                        contextMenuActions.addNewActionToQuestion(
                                            resourceId,
                                        )
                                        break
                                    case "Finding":
                                        contextMenuActions.addNewActionToFinding(
                                            resourceId,
                                        )
                                        break
                                }
                                hideModal()
                            }}
                        >
                            {Symbols.Create} {labels.AddNewAction}
                        </Action>
                    )}

                    {!!canLinkExistingAction && (
                        <Action
                            actionType={ActionType.block}
                            extraClasses={buttonAlignmentClasses}
                            title={labels.LinkExistingAction}
                            data-ft-component="linkExistingAction"
                            onClick={(): void => {
                                switch (type) {
                                    case "Topic":
                                        contextMenuActions.linkExistingActionToTopic(
                                            resourceId,
                                        )
                                        break
                                    case "Question":
                                        contextMenuActions.linkExistingActionToQuestion(
                                            resourceId,
                                        )
                                        break
                                    case "Finding":
                                        contextMenuActions.linkExistingActionToFinding(
                                            resourceId,
                                        )
                                        break
                                }
                                hideModal()
                            }}
                        >
                            {Symbols.Link} {labels.LinkExistingAction}
                        </Action>
                    )}

                    {hasSecondSeparator && separator}

                    {!isFinding && !!canCreateNewFinding && (
                        <Action
                            actionType={ActionType.block}
                            extraClasses={buttonAlignmentClasses}
                            title={labels.AddNewFinding}
                            data-ft-component="addNewFinding"
                            onClick={(): void => {
                                switch (type) {
                                    case "Topic":
                                        contextMenuActions.addNewFindingToTopic(
                                            resourceId,
                                        )
                                        break
                                    case "Question":
                                        contextMenuActions.addNewFindingToQuestion(
                                            resourceId,
                                        )
                                        break
                                }
                                hideModal()
                            }}
                        >
                            {Symbols.Create} {labels.AddNewFinding}
                        </Action>
                    )}

                    {!isFinding && !!canLinkExistingFinding && (
                        <Action
                            actionType={ActionType.block}
                            extraClasses={buttonAlignmentClasses}
                            title={labels.LinkExistingFinding}
                            data-ft-component="linkExistingFinding"
                            onClick={(): void => {
                                switch (type) {
                                    case "Topic":
                                        contextMenuActions.linkExistingFindingToTopic(
                                            resourceId,
                                        )
                                        break
                                    case "Question":
                                        contextMenuActions.linkExistingFindingToQuestion(
                                            resourceId,
                                        )
                                        break
                                }
                                hideModal()
                            }}
                        >
                            {Symbols.Link} {labels.LinkExistingFinding}
                        </Action>
                    )}
                </div>
            </Modal>
        </>
    )
}
